import { useEffect, useState } from "react";
import { CloseButton, StatusMessage } from "../../style/view.styles";
import LoadingSpinner from "./LoadingSpinner";
import { fetchEntityData, saveEntityData } from "../../utils/RequestHandler";
import { getLoggedInUser } from "../../utils/AuthHandler";
import {
  FormContainer,
  StyledForm,
  StyledFormField,
  StyledInput,
  StyledLabel,
  StyledSelect,
  StyledTextarea,
  Subtitle,
  Title,
} from "../../style/modal.form.style";
import {
  FaBriefcase,
  FaCalendarAlt,
  FaLaptopHouse,
  FaMoneyBillWave,
} from "react-icons/fa";
import FullButton from "../Buttons/FullButton";

const AddInternshipForm = ({ handleModalClose, onSuccess }) => {
  const [formStatus, setFormStatus] = useState({ message: "", type: "" });
  const [submitting, setSubmitting] = useState(false);
  const [organizationId, setOrganizationId] = useState(null);
  const [compensationTypes, setCompensationTypes] = useState([]);
  const [workModes, setWorkModes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    position: "",
    description: "",
    work_mode: "",
    start_date: "",
    end_date: "",
    compensation_type: "",
    organization: "",
  });

  useEffect(() => {
    const fetchCompensationType = async () => {
      try {
        const response = await fetchEntityData("compensation_type");
        if (response.success) {
          setCompensationTypes(response.result);
        }
      } catch (error) {
        console.error(error);
      }
    };
    const fetchWorkModes = async () => {
      try {
        const response = await fetchEntityData("program_delivery_types");
        if (response.success) {
          setWorkModes(response.result);
        }
      } catch (error) {
        console.error(error);
      }
    };
    const setOrganization = async () => {
      try {
        const user = getLoggedInUser();
        const response = await fetchEntityData("organizations");
        if (!response?.success || !Array.isArray(response.result)) {
          throw new Error("Invalid response format or request failed");
        }

        const data = response.result.find(
          (record) => record.added_by === user.username
        );
        setOrganizationId(data.id);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompensationType();
    fetchWorkModes();
    setOrganization();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const requestData = {
        ...formData,
        organization: organizationId,
      };
      const response = await saveEntityData("internships", requestData, true);
      if (response.success) {
        setFormStatus({
          message: "Interships added successfully!",
          type: "success",
        });
        if (onSuccess) onSuccess();
      } else {
        setFormStatus({
          message:
            response.message ??
            "An error occurred while submitting your request. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setFormStatus({
        message:
          "An error occurred while adding your interships. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormContainer>
      <CloseButton onClick={handleModalClose}>X</CloseButton>
      <Title>Add Internship Position</Title>
      <Subtitle>
        Fill in the form below to post a new internship position for talents to
        apply.
      </Subtitle>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <StyledForm>
          <StyledFormField>
            <StyledLabel>
              <FaBriefcase style={{ marginRight: 5 }} /> Position
            </StyledLabel>
            <StyledInput
              type="text"
              name="position"
              placeholder="Enter intern position"
              value={formData.position ?? ""}
              onChange={(e) =>
                setFormData({ ...formData, position: e.target.value })
              }
              required
            />
          </StyledFormField>

          <StyledFormField>
            <StyledLabel>
              <FaLaptopHouse style={{ marginRight: 5 }} /> Work Mode
            </StyledLabel>
            <StyledSelect
              value={formData.work_mode ?? ""}
              onChange={(e) =>
                setFormData({ ...formData, work_mode: e.target.value })
              }
            >
              <option value="">Select work mode</option>
              {workModes.map((option) => (
                <option key={option.type} value={option.id}>
                  {option.type}
                </option>
              ))}
            </StyledSelect>
          </StyledFormField>

          <StyledFormField fullWidth>
            <StyledLabel>
              <FaBriefcase style={{ marginRight: 5 }} /> Description
            </StyledLabel>
            <StyledTextarea
              name="description"
              placeholder="Enter opportunity description"
              rows="5"
              value={formData.description}
              onChange={handleInputChange}
              required
            />
          </StyledFormField>

          <StyledFormField>
            <StyledLabel>
              <FaCalendarAlt style={{ marginRight: 5 }} /> Start Date
            </StyledLabel>
            <StyledInput
              type="date"
              name="start_date"
              value={formData.start_date}
              onChange={handleInputChange}
              required
            />
          </StyledFormField>

          <StyledFormField>
            <StyledLabel>
              <FaCalendarAlt style={{ marginRight: 5 }} /> End Date
            </StyledLabel>
            <StyledInput
              type="date"
              name="end_date"
              value={formData.end_date}
              onChange={handleInputChange}
              required
            />
          </StyledFormField>

          <StyledFormField>
            <StyledLabel>
              <FaMoneyBillWave style={{ marginRight: 5 }} /> Compensation Type
            </StyledLabel>
            <StyledSelect
              value={formData.compensation_type ?? ""}
              onChange={(e) =>
                setFormData({ ...formData, compensation_type: e.target.value })
              }
            >
              <option value="">Select compensation type</option>
              {compensationTypes.map((option) => (
                <option key={option.type} value={option.id}>
                  {option.type}
                </option>
              ))}
            </StyledSelect>
          </StyledFormField>
        </StyledForm>
      )}
      <FullButton
        type="submit"
        disabled={submitting}
        action={handleSubmit}
        title={submitting ? "Saving..." : "Save Internship"}
      />

      {formStatus.message && (
        <StatusMessage type={formStatus.type}>
          {formStatus.message}
        </StatusMessage>
      )}
    </FormContainer>
  );
};

export default AddInternshipForm;
